import { useEffect, useMemo, useRef, useState } from 'react'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { AppDispatch, RootState } from '@/store/store'
import { selectMatterById } from '@/matter/store/selectors'
import {
  selectChildFolders,
  selectChildFoldersRequest,
  selectFolderFetchFailed,
  selectFolderFetchPending,
  selectFoldersRecord,
  selectRootFolderIdForMatter,
  selectRootFolderRequestForMatter,
} from '@/user_files/folders/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { getFolderAncestorIds } from '@/user_files/folders/utils/get-folder-ancestor-ids'
import { selectFileListRequest, selectFilesForFolder } from '@/user_files/files/store/selectors'
import { UserFilesActions } from '@/user_files/files/store/slice'
import type { FolderResponse } from '@/user_files/folders/schemas'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'

type UseBrowseFilesParams = {
  /** Matter whose files are listed. */
  matterId: string
  /** Whether the modal is open. */
  open: boolean
  /** Optional folder id to open the browse UI on; otherwise browsing starts at the matter root. */
  initialFolderId?: string
  /** When true, keep loading/error state on `initialFolderId` instead of falling back to the matter root. */
  initialFolderRequired?: boolean
  /** If true, disables navigation. */
  disabled?: boolean
  /** When true, refresh folder children from the backend whenever a folder is opened. */
  refreshOnOpen?: boolean
  /** Called after any folder navigation (breadcrumbs or folder list). */
  onNavigate?: () => void
}

export type UseBrowseFilesReturn = {
  /** Breadcrumb trail for the current browse path within the matter. */
  breadcrumbItems: BreadcrumbItemType[]
  /** Id of the currently browsed folder. */
  folderId: string | null
  /** Child folders of the browsed folder. */
  folders: FolderResponse[]
  /** Files in the browsed folder. */
  files: UserFileRead[]
  /** True while the browsed folder’s data is loading. */
  loading: boolean
  /** User-facing message when folder or file list fetch failed. */
  error: string | undefined
  /** Refetch after a load failure. */
  retry: () => void
  /** Open a subfolder by id. */
  onNavigateToFolder: (folderId: string) => void
}

export function useBrowseFiles({
  matterId,
  open,
  initialFolderId,
  initialFolderRequired = false,
  disabled = false,
  refreshOnOpen = false,
  onNavigate,
}: UseBrowseFilesParams): UseBrowseFilesReturn {
  const dispatch: AppDispatch = useAppDispatch()

  const [browseFolderId, setBrowseFolderId] = useState<string | null>(null)
  // Which initial folder id we've already dispatched a fetch for (per open), so the fetch effect
  // doesn't re-dispatch on every render while the request is in flight.
  const requestedInitialFolderIdRef = useRef<string | null>(null)

  const navigate = (folderId: string) => {
    setBrowseFolderId(folderId)
    onNavigate?.()
  }

  const matterRecord = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  const matterName = matterRecord?.name ?? 'Matter'

  const foldersById = useAppSelector(selectFoldersRecord)
  const browseFolderFiles = useAppSelector((state: RootState) => selectFilesForFolder(state, { folderId: browseFolderId }))

  const rootFolderId = useAppSelector((state: RootState) => selectRootFolderIdForMatter(state, { matterId }))
  const rootFolderRequest = useAppSelector((state: RootState) => selectRootFolderRequestForMatter(state, { matterId }))

  const childFolders = useAppSelector((state: RootState) => selectChildFolders(state, { parentId: browseFolderId }))
  const childFoldersRequest = useAppSelector((state: RootState) => selectChildFoldersRequest(state, { parentId: browseFolderId }))

  const browseFolderState = browseFolderId ? foldersById[browseFolderId] : undefined
  const initialFolderState = initialFolderId ? foldersById[initialFolderId] : undefined
  const initialFolderHasWrongMatter = !!initialFolderState && initialFolderState.data.matter_id !== matterId
  const initialFolderResolved = !!initialFolderState && !initialFolderHasWrongMatter
  const initialFolderPending = useAppSelector((state: RootState) => selectFolderFetchPending(state, { folderId: initialFolderId }))
  // A `fetchFolderById` that errored leaves an entry in the store; combined with the folder not
  // being resolved for this matter (and no retry in flight), that is a load failure only after this
  // open has attempted the initial-folder fetch. A pending retry supersedes the stale errored entry.
  const initialFolderStoreFetchFailed = useAppSelector((state: RootState) => selectFolderFetchFailed(state, { folderId: initialFolderId }))
  const initialFolderFetchAttempted = requestedInitialFolderIdRef.current === initialFolderId
  const initialFolderLoadFailed =
    !initialFolderResolved && !initialFolderPending && (initialFolderHasWrongMatter || (initialFolderStoreFetchFailed && initialFolderFetchAttempted))
  const files = useMemo(() => browseFolderFiles.filter((file) => !file.is_deleted), [browseFolderFiles])
  const fileListRequest = useAppSelector((state: RootState) => selectFileListRequest(state, { folderId: browseFolderId }))
  const processingFileIds = useMemo(
    () =>
      files
        .filter((file) => file.processing_status === UserFileProcessingStatus.Pending || file.processing_status === UserFileProcessingStatus.Processing)
        .map((file) => file.id),
    [files]
  )
  useUserFilesPollingProcessor({
    fileIds: processingFileIds,
    enabled: open && processingFileIds.length > 0,
  })

  const isRoot = browseFolderState?.data.is_root ?? false
  const rootFolderStatus = rootFolderRequest.status
  const rootFolderHasError = rootFolderStatus === 'error'

  // Reset folder state when the modal is closed.
  useEffect(() => {
    if (!open) return
    setBrowseFolderId(null)
    requestedInitialFolderIdRef.current = null
    dispatch(UserFoldersActions.fetchRootFolder({ matterId }))
  }, [open, matterId, dispatch])

  // Fetch a required initial folder that isn't already in the store, via the shared folder store.
  // Its loading/error state is read back from the store (pending / failed request), so no local
  // request bookkeeping is needed beyond guarding against re-dispatching an in-flight fetch.
  useEffect(() => {
    if (!open || !initialFolderId || !initialFolderRequired || browseFolderId || initialFolderResolved || initialFolderLoadFailed) return
    if (requestedInitialFolderIdRef.current === initialFolderId || initialFolderPending) return

    requestedInitialFolderIdRef.current = initialFolderId
    dispatch(UserFoldersActions.fetchFolderById({ folderId: initialFolderId }))
  }, [open, initialFolderId, initialFolderRequired, browseFolderId, initialFolderResolved, initialFolderLoadFailed, initialFolderPending, dispatch])

  // Pick initial folder: prefer `initialFolderId` when it exists in the store for this matter, else matter root.
  useEffect(() => {
    if (!open || browseFolderId) return

    if (initialFolderId) {
      if (initialFolderState?.data.matter_id === matterId) {
        setBrowseFolderId(initialFolderId)
        return
      }
      if (initialFolderRequired) return
    }

    if (!rootFolderId || rootFolderHasError) return
    setBrowseFolderId(rootFolderId)
  }, [open, rootFolderId, browseFolderId, initialFolderId, initialFolderRequired, initialFolderState, matterId, rootFolderHasError])

  // Fetch folder children and files when the folder is changed.
  useEffect(() => {
    if (!open || !browseFolderId) return
    dispatch(UserFoldersActions.fetchFolderChildren(refreshOnOpen ? { folderId: browseFolderId, force: true } : { folderId: browseFolderId }))
    dispatch(UserFilesActions.fetchFilesForFolder({ folderId: browseFolderId }))
  }, [open, browseFolderId, refreshOnOpen, dispatch])

  const matterItem: BreadcrumbItemType = {
    id: matterId,
    label: matterName,
    onClick: isRoot || disabled || rootFolderHasError || !rootFolderId ? undefined : () => navigate(rootFolderId),
  }

  const breadcrumbItems: BreadcrumbItemType[] = isRoot
    ? [matterItem]
    : [
        matterItem,
        ...getFolderAncestorIds(foldersById, browseFolderId ?? '')
          .filter((id) => !foldersById[id]?.data.is_root)
          .map((folderId, index, arr) => ({
            id: folderId,
            label: foldersById[folderId]?.data.name ?? '',
            onClick: index === arr.length - 1 || disabled ? undefined : () => navigate(folderId),
          })),
      ]

  const rootFetchFailed = !browseFolderId && rootFolderHasError
  const childOrFileFetchFailed = !!browseFolderId && (childFoldersRequest.status === 'error' || fileListRequest.status === 'error')
  const initialFolderFetchFailed = !browseFolderId && initialFolderRequired && initialFolderLoadFailed
  const hasError = rootFetchFailed || childOrFileFetchFailed || initialFolderFetchFailed

  // The required initial folder loads independently of the matter root, so its wait does not gate on rootFolderId.
  const waitingForInitialFolder = open && !browseFolderId && initialFolderRequired && !!initialFolderId && !initialFolderFetchFailed
  const waitingForRoot =
    open &&
    !browseFolderId &&
    !initialFolderRequired &&
    !rootFolderHasError &&
    (!rootFolderId || rootFolderStatus === 'loading' || (rootFolderStatus === 'success' && !!rootFolderId))

  const currentFolderListSettled = !!browseFolderId && (hasError || (childFoldersRequest.status === 'success' && fileListRequest.status === 'success'))

  const loading = waitingForInitialFolder || waitingForRoot || (!!browseFolderId && !currentFolderListSettled)

  const retry = () => {
    if (!open) return
    if (initialFolderFetchFailed && initialFolderId) {
      requestedInitialFolderIdRef.current = initialFolderId
      dispatch(UserFoldersActions.fetchFolderById({ folderId: initialFolderId }))
      return
    }
    if (!browseFolderId) {
      dispatch(UserFoldersActions.fetchRootFolder({ matterId, force: true }))
      return
    }
    dispatch(UserFoldersActions.fetchFolderChildren({ folderId: browseFolderId, force: true }))
    dispatch(UserFilesActions.fetchFilesForFolder({ folderId: browseFolderId }))
  }

  return {
    breadcrumbItems,
    folderId: browseFolderId,
    folders: childFolders,
    files,
    loading,
    error: hasError ? 'Unable to load files.' : undefined,
    retry,
    onNavigateToFolder: navigate,
  }
}
